import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { compact } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { Controller, useForm } from 'react-hook-form';
import { Button, Callout, ConfirmationModal, FunctionalLoader, MenuItem, SplitButton, TextField, useStateInRef, useDevice, Row, Badge, CircularLoader } from 'seagull';
import { Archive, BookmarkAdd, Calendar, Copy, DocumentCheckmark, EmailSent, LinkIcon, Send, Trash } from 'seagull/icons';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { validateReviewers } from '~/business-logic/draftLogic';
import { templateNameValidation } from '~/business-logic/validators';
import { AgentDraftingPane } from '~/components/ai/AgentDraftingPane/AgentDraftingPane';
import { routes } from '~/constants';
import { useEventReporter, useOverlayContext, useIoContext, useStoreActions } from '~/contexts';
import { useContactsMap } from '~/hooks/reactQuery/derived/useContactsMap';
import { useArchiveDraft } from '~/hooks/reactQuery/mutations/useArchiveDraft';
import { useCancelEditProposal } from '~/hooks/reactQuery/mutations/useCancelEditProposal';
import { useRepublishProposal } from '~/hooks/reactQuery/mutations/useRepublishProposal';
import { useAgreements } from '~/hooks/reactQuery/queries/useAgreements';
import { useDraft } from '~/hooks/reactQuery/queries/useDraft';
import { useLegalTerms } from '~/hooks/reactQuery/queries/useLegalTerms';
import { useProposals } from '~/hooks/reactQuery/queries/useProposals';
import { useCopyProposalLink } from '~/hooks/useCopyProposalLink';
import { useCreateRelationshipFromDraft } from '~/hooks/useCreateRelationshipFromDraft';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { useScheduledProposal } from '~/hooks/useScheduledProposal';
import { useSetWorksAtOptimisticUpdate } from '~/hooks/useSetWorksAtOptimisticUpdate';
import { useSignal } from '~/hooks/useSignal';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { makeNetTerms } from '~/infra/translator';
import { handleRequest, sendErrReport } from '~/infra/utils';
import { agreementStudioAgreementsArgs } from '~/pages/AgreementsDashboard/agreementsDashboardFilters';
import { helpArticles } from '~/utils/helpArticles';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { ClientPreview } from '../../ClientPreview/ClientPreview';
import ConfirmExternallySignModal from '../../ConfirmExternallySignModal/ConfirmExternallySignModal';
import ExternalLinkButton from '../../ExternalLinkButton';
import { RelationshipEffectiveDate } from '../../RelationshipEffectiveDate/RelationshipEffectiveDate';
import { AgreementName } from '../AgreementName/AgreementName';
import { AGENT_TARGET_EFFECTIVE_DATE } from '../AgreementStudioModal/AgentChangeHighlight/agentTargetSets';
import AgreementStudioModal, { SubmitType } from '../AgreementStudioModal/AgreementStudioModal';
import { draftToCommonWithId } from '../AgreementStudioModal/AgreementStudioModal.utils';
import styles from '../AgreementStudioPage.module.scss';
import { contactHasAgreement } from '../AgreementStudioPage.utils';
import { AgreementStudioShell } from '../AgreementStudioShell/AgreementStudioShell';
import { useStudioCloseGate } from '../hooks/useStudioCloseGate';
import { useStudioUiState } from '../useStudioUiState';
import { hasBilledImmediatelyService, makeDraft } from './AgreementStudioProposalMode.utils';
import { PostAgreementCreation } from './PostAgreementCreation';
import ReadyToSendModal from './ReadyToSendModal/ReadyToSendModal';
import { Recipients } from './Recipients/Recipients';
import ScheduleProposal from './ScheduleProposal/ScheduleProposal';
import { CopyLinksModal } from './SilentProposal/CopyLinksModal';
import { GenerateLinkModal } from './SilentProposal/GenerateLinkModal';
const createRelationshipLoader = 'createRelationship';
var DraftStatus = /*#__PURE__*/ function(DraftStatus) {
    DraftStatus["Loading"] = "loading";
    DraftStatus["Saved"] = "saved";
    DraftStatus["Error"] = "error";
    DraftStatus["None"] = "none";
    return DraftStatus;
}(DraftStatus || {});
function SaveAsTemplateModal(param) {
    let { draftId, dismissModal } = param;
    const { api } = useStoreActions();
    const { showSuccessToast, showErrorToast } = useOverlayContext();
    const { t: proposalModeTexts } = useLocaleKeys('agreementStudio.proposalMode');
    const { control, handleSubmit } = useForm({
        mode: 'all',
        defaultValues: {
            name: ''
        }
    });
    const [loading, setLoading] = useState(false);
    const closeModal = ()=>dismissModal(false);
    async function submit(param) {
        let { name } = param;
        setLoading(true);
        const result = await handleRequest(api.proposalTemplates.createFromDraft(draftId, name));
        if (result.success) {
            showSuccessToast(proposalModeTexts.saveAsTemplateSavedToast(), 'proposal-template-saved-toast');
            dismissModal(true);
        } else {
            if (new ErrorResponse(result.error).hasServerError(ServerError.NameAlreadyExists)) {
                showErrorToast(proposalModeTexts.saveAsTemplateNameExistsToast(), 'proposal-template-name-already-used');
            } else {
                showErrorToast(proposalModeTexts.saveAsTemplateErrorToast(), 'proposal-template-not-saved-toast');
            }
        }
        setLoading(false);
    }
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'agreement_studio_proposal_mode_1_confirm'
            },
            secondaryButton: {
                name: 'agreement_studio_proposal_mode_1_cancel'
            }
        },
        title: proposalModeTexts.saveAsTemplateModalTitle(),
        testId: "save-as-proposal-template-modal",
        content: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Controller, {
                    name: "name",
                    control: control,
                    rules: templateNameValidation,
                    render: (p)=>/*#__PURE__*/ _jsx(TextField, _object_spread({
                            bi: {
                                name: 'proposal_template_name'
                            },
                            collapseHelperText: false,
                            autoFocus: true,
                            testId: "proposal-template-name",
                            label: proposalModeTexts.saveAsTemplateModalTemplateNameLabel()
                        }, connectControllerToTextField(p)))
                }),
                /*#__PURE__*/ _jsx(Callout, {
                    style: {
                        marginTop: '32px'
                    },
                    children: proposalModeTexts.saveAsTemplateModalCallout()
                })
            ]
        }),
        primaryButtonLabel: proposalModeTexts.saveAsTemplateModalSaveButton(),
        primaryButtonLoading: loading,
        onPrimaryButtonClick: handleSubmit(submit),
        secondaryButtonLabel: proposalModeTexts.saveAsTemplateModalCancelButton(),
        onSecondaryButtonClick: closeModal,
        onCloseButtonClick: closeModal
    });
}
function getSavingStatusMP(draftStatus, texts) {
    const testId = "save-indicator-".concat(draftStatus);
    switch(draftStatus){
        case "none":
            return null;
        case "loading":
            return /*#__PURE__*/ _jsx(CircularLoader, {});
        case "saved":
            return /*#__PURE__*/ _jsx(Badge, {
                skin: "success",
                rounded: true,
                outlined: true,
                testId: testId,
                children: texts.draftSaved()
            });
        case "error":
            return /*#__PURE__*/ _jsx(Badge, {
                skin: "error",
                rounded: true,
                outlined: true,
                testId: testId,
                children: texts.draftSaveError()
            });
    }
}
export const AgreementStudioProposalModeTestIds = pathgen('AgreementStudioProposalModeTestIds');
export function AgreementStudioProposalMode(props) {
    var _ref, _ref1, _ref2, _ref3, _ref4;
    const [adoptedDraftId, setAdoptedDraftId] = useState(undefined);
    const { draft: adoptedDraft, forceRefetch: forceRefetchAdoptedDraft } = useDraft({
        draftId: adoptedDraftId,
        refreshOnHookMount: true
    });
    const effectiveDraft = (_ref = props.viewProposalMode ? props.viewProposalMode.proposalAsDraft : adoptedDraft !== null && adoptedDraft !== void 0 ? adoptedDraft : props.draft) !== null && _ref !== void 0 ? _ref : null;
    const onAgentDraftCreated = async (id)=>{
        if (adoptedDraftId !== id) {
            setAdoptedDraftId(id);
            window.history.replaceState(window.history.state, 'Draft', routes.draft(id));
        }
        await forceRefetchAdoptedDraft(id);
    };
    const { isMobileDevice } = useDevice();
    const { HELP_ME_DRAFT } = useFeatureFlags();
    const isViewProposal = !!props.viewProposalMode;
    const locationState = useLocation().state;
    const hasInitialConversationId = typeof (locationState === null || locationState === void 0 ? void 0 : locationState.conversationId) === 'string' && locationState.conversationId.length > 0;
    const [agentPaneIsOpen, setAgentPaneIsOpen] = useState(!props.draft || hasInitialConversationId);
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [autoSaveDraftId, setAutoSaveDraftId] = useState(undefined);
    const [lastTurnChangedTargets, setLastTurnChangedTargets] = useState([]);
    const shouldRenderAgentDraftingPane = HELP_ME_DRAFT && !isMobileDevice && !isViewProposal;
    const onAgentTurnApplied = async (changedTargets)=>{
        var _ref;
        setLastTurnChangedTargets(changedTargets);
        const currentDraftId = (_ref = effectiveDraft === null || effectiveDraft === void 0 ? void 0 : effectiveDraft.id()) !== null && _ref !== void 0 ? _ref : autoSaveDraftId;
        if (currentDraftId != null) await forceRefetchAdoptedDraft(currentDraftId);
    };
    const agentPaneContent = shouldRenderAgentDraftingPane ? /*#__PURE__*/ _jsx(AgentDraftingPane, {
        draftId: (_ref1 = effectiveDraft === null || effectiveDraft === void 0 ? void 0 : effectiveDraft.id()) !== null && _ref1 !== void 0 ? _ref1 : autoSaveDraftId,
        onDraftCreated: onAgentDraftCreated,
        onTurnApplied: onAgentTurnApplied,
        isOpen: agentPaneIsOpen,
        onOpenChange: setAgentPaneIsOpen,
        isAutoSaving: isAutoSaving
    }) : null;
    const overlayContext = useOverlayContext();
    const { t: editInPlaceTexts } = useLocaleKeys('agreementStudio.editInPlace');
    const linkedRelationshipId = (_ref2 = effectiveDraft === null || effectiveDraft === void 0 ? void 0 : effectiveDraft.linkedRelationshipId()) !== null && _ref2 !== void 0 ? _ref2 : null;
    const scrollToTopRef = useRef(null);
    const { requestClose, setHasUnsavedChanges } = useStudioCloseGate({
        onClose: props.onClose,
        shouldClose: linkedRelationshipId != null ? ()=>overlayContext.showModal({
                biName: 'agreement_studio_proposal_mode_edit_in_place_close_confirmation_confirm',
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(ConfirmationModal, {
                        bi: {
                            primaryButton: {
                                name: 'agreement_studio_proposal_mode_2_confirm'
                            },
                            secondaryButton: {
                                name: 'agreement_studio_proposal_mode_2_cancel'
                            }
                        },
                        testId: "close-edit-in-place-confirmation-modal",
                        title: editInPlaceTexts.closeConfirmationTitle(),
                        content: editInPlaceTexts.closeConfirmationContent(),
                        primaryButtonLabel: editInPlaceTexts.closeConfirmationPrimaryButton(),
                        secondaryButtonLabel: editInPlaceTexts.closeConfirmationSecondaryButton(),
                        onPrimaryButtonClick: ()=>dismissModal(true),
                        onSecondaryButtonClick: ()=>dismissModal(false),
                        onCloseButtonClick: ()=>dismissModal(false)
                    });
                }
            }) : undefined
    });
    const bodyKey = "".concat((_ref3 = effectiveDraft === null || effectiveDraft === void 0 ? void 0 : effectiveDraft.id()) !== null && _ref3 !== void 0 ? _ref3 : 'new', "#").concat((_ref4 = effectiveDraft === null || effectiveDraft === void 0 ? void 0 : effectiveDraft.lastModifiedOn()) !== null && _ref4 !== void 0 ? _ref4 : 0);
    // Lives above the keyed body so toggle state survives draft-driven remounts.
    const studioUiState = useStudioUiState();
    return /*#__PURE__*/ _jsx(AgreementStudioShell, {
        onClose: requestClose,
        rightPane: agentPaneContent != null ? {
            element: agentPaneContent,
            isOpen: agentPaneIsOpen
        } : undefined,
        doScrollToTopRef: scrollToTopRef,
        contentKey: bodyKey,
        children: /*#__PURE__*/ _jsx(AgreementStudioProposalModeBody, _object_spread_props(_object_spread({}, props), {
            draft: effectiveDraft,
            onAutoSavingChange: setIsAutoSaving,
            onDraftIdGenerated: setAutoSaveDraftId,
            onUnsavedChangesChange: setHasUnsavedChanges,
            scrollToTopRef: scrollToTopRef,
            uiState: studioUiState,
            changedTargets: lastTurnChangedTargets
        }), bodyKey)
    });
}
function AgreementStudioProposalModeBody(allProps) {
    var _ref, _ref1, _ref2, _ref3;
    const { onAutoSavingChange, onDraftIdGenerated, onUnsavedChangesChange, changedTargets } = allProps, props = _object_without_properties(allProps, [
        "onAutoSavingChange",
        "onDraftIdGenerated",
        "onUnsavedChangesChange",
        "changedTargets"
    ]);
    const { isMobileDevice } = useDevice();
    const { UPDATE_DEFAULT_AGREEMENT_TITLE } = useFeatureFlags();
    const { viewProposalMode } = props;
    const { archive } = useArchiveDraft({
        withPageLoader: true,
        onSuccess: ()=>history.push(routes.draftsDashboard),
        biCode: 18801
    });
    const isViewProposal = !!viewProposalMode;
    const draft = props.draft;
    const locationState = useLocation().state;
    const { reportToAppcues, reportToGtm, reportToHubspot, reportToHeap, prepareFormObject, reportProductsFormSubmission, reportButtonClick } = useEventReporter();
    const history = useHistory();
    const { api, logBI } = useStoreActions();
    const overlayContext = useOverlayContext();
    const { pushBottomSheetPage, popBottomSheetPage } = overlayContext;
    const { legalTerms: legalTermsDocuments, isLoading: legalTermsLoading, error: legalTermsError } = useLegalTerms();
    const { agreements } = useAgreements(agreementStudioAgreementsArgs);
    const { proposals } = useProposals();
    const createRelationshipFromDraft = useCreateRelationshipFromDraft();
    const updateWorksAt = useSetWorksAtOptimisticUpdate();
    const [newContactValid, setNewContactValid] = useState(true);
    const [titleValid, setTitleValid] = useState((draft === null || draft === void 0 ? void 0 : draft.title()) != null);
    const [clientContactId, setClientContactId] = useState((_ref = draft === null || draft === void 0 ? void 0 : draft.clientContactId()) !== null && _ref !== void 0 ? _ref : locationState === null || locationState === void 0 ? void 0 : locationState.clientContactId);
    const [showAgreementTitleWarning, setShowAgreementTitleWarning] = useState(false);
    const [isSampleRelationship, setIsSampleRelationship] = useState((_ref1 = (_ref2 = draft === null || draft === void 0 ? void 0 : draft.isSample()) !== null && _ref2 !== void 0 ? _ref2 : locationState === null || locationState === void 0 ? void 0 : locationState.defaultToSampleContact) !== null && _ref1 !== void 0 ? _ref1 : false);
    const [effectiveDate, setEffectiveDate] = useState(()=>(draft === null || draft === void 0 ? void 0 : draft.effectiveDateAsDate()) || null);
    const [showCustomerDetailsWarning, setShowCustomerDetailsWarning] = useState(false);
    const [draftId, setDraftId] = useState(draft === null || draft === void 0 ? void 0 : draft.id());
    const getDraftId = useStateInRef(draftId);
    const notifiedDraftIdRef = useRef(null);
    const [reviewers, setReviewers] = useState(draft ? draft.reviewers() : []);
    const [showReviewerErrors, signalShowReviewerErrors] = useSignal();
    const { getNow } = useIoContext();
    const { contactsMap, sampleContact, fetchContacts } = useContactsMap(props.worksAt.id);
    const { showSwitchToEditModal } = useScheduledProposal({
        draftId: draftId,
        isSampleRelationship,
        refresh: ()=>{},
        onCloseCancelSchedulingModal: ()=>history.push(routes.draftsDashboard)
    });
    const [draftStatus, setDraftStatus] = useState("none");
    const existingContact = clientContactId ? contactsMap[clientContactId] : isSampleRelationship ? sampleContact : undefined;
    const { t: commonTexts } = useLocaleKeys('common');
    const defaultAgreementTitle = UPDATE_DEFAULT_AGREEMENT_TITLE ? commonTexts.defaultAgreementTitle() : commonTexts.untitledAgreement();
    const [agreementTitle, setAgreementTitle] = useState((draft === null || draft === void 0 ? void 0 : draft.title()) || defaultAgreementTitle);
    useOnMount(()=>{
        if (draft === null || draft === void 0 ? void 0 : draft.isScheduled()) {
            showSwitchToEditModal();
        }
    });
    const showAgreementCreationToast = ()=>overlayContext.showSuccessToast(studioTexts.agreementSentSuccess());
    const navigationAfterCreation = (redirectUrl)=>{
        const url = redirectUrl || routes.proposalsDashboard;
        if (url === routes.agreementStudio) {
            history.push(url, {
                fromPostAgreement: true
            });
            history.go(0);
        } else {
            history.push(url);
        }
    };
    const { getAgreementNotificationLinks } = useCopyProposalLink();
    const draftToCommon = (draft)=>draftToCommonWithId(draft, generateUniqueId('proposal-template'));
    const linkedRelationshipId = (_ref3 = draft === null || draft === void 0 ? void 0 : draft.linkedRelationshipId()) !== null && _ref3 !== void 0 ? _ref3 : null;
    const { t: editInPlaceTexts } = useLocaleKeys('agreementStudio.editInPlace');
    const { t: proposalModeTexts } = useLocaleKeys('agreementStudio.proposalMode');
    const { t: actionsTexts } = useLocaleKeys('agreementStudio.proposalMode.actions');
    const { t: studioTexts } = useLocaleKeys('components.agreementStudio');
    const { republishProposal } = useRepublishProposal({
        onSuccess: ()=>history.push(routes.proposalsDashboard)
    });
    const { cancelEditProposal } = useCancelEditProposal({
        onSuccess: ()=>history.push(routes.proposalsDashboard)
    });
    const discardDraft = async ()=>{
        const loaderName = 'discardDraft';
        overlayContext.startLoader(loaderName);
        try {
            await api.draft.deleteById(draftId);
            history.push(routes.draftsDashboard);
        } finally{
            overlayContext.stopLoader(loaderName);
        }
    };
    const genDraftId = useCallback(()=>{
        const existing = getDraftId();
        if (existing) return existing;
        const id = generateUniqueId('draft');
        setDraftId(id);
        window.history.replaceState('', 'Draft', routes.draft(id));
        return id;
    }, [
        getDraftId
    ]);
    const getDraft = (data, titleOverride)=>{
        return makeDraft(_object_spread_props(_object_spread({}, data), {
            id: genDraftId(),
            title: titleOverride !== null && titleOverride !== void 0 ? titleOverride : agreementTitle,
            clientContactId,
            isSample: isSampleRelationship,
            effectiveDate,
            reviewers,
            metadata: draft === null || draft === void 0 ? void 0 : draft.metadata(),
            linkedRelationshipId
        }));
    };
    const saveDraft = async (data, isMounted)=>{
        if (!isMounted()) return;
        onAutoSavingChange(true);
        setDraftStatus("loading");
        const draftToSave = getDraft(data);
        const result = await handleRequest(api.draft.save(draftToSave));
        if (!isMounted()) return;
        onAutoSavingChange(false);
        if (result.success && notifiedDraftIdRef.current !== draftToSave.id) {
            notifiedDraftIdRef.current = draftToSave.id;
            onDraftIdGenerated(draftToSave.id);
        }
        setDraftStatus(result.success ? "saved" : "error");
    };
    async function submit(data, submitType) {
        var _props_worksAt_key;
        if (linkedRelationshipId != null) {
            republishProposal({
                draft: getDraft(data),
                relationshipId: linkedRelationshipId
            });
            return;
        }
        const sendSilently = submitType === SubmitType.SendSilently;
        const externallySign = submitType === SubmitType.ExternallySign;
        const firstName = existingContact ? existingContact.firstName() : '';
        const lastName = existingContact ? existingContact.lastName() : '';
        const companyName = existingContact ? existingContact.companyName() : '';
        const email = existingContact ? existingContact.email() : '';
        reportButtonClick('agreement-studio-topbar-btn-submit');
        logBI(10485);
        if (!newContactValid || !firstName || firstName.length < 2 || !lastName || lastName.length < 2 || !companyName) {
            var _props_scrollToTopRef_current, _props_scrollToTopRef;
            (_props_scrollToTopRef_current = (_props_scrollToTopRef = props.scrollToTopRef).current) === null || _props_scrollToTopRef_current === void 0 ? void 0 : _props_scrollToTopRef_current.call(_props_scrollToTopRef);
            setShowCustomerDetailsWarning(true);
            return;
        }
        if (!validateReviewers(reviewers, existingContact)) {
            signalShowReviewerErrors(true);
            return;
        }
        if (!titleValid) {
            setShowAgreementTitleWarning(true);
            return;
        }
        if (submitType === SubmitType.ExternallySign && isSampleRelationship) {
            overlayContext.showErrorToast('You cannot "approve on behalf of your client" for a sample contact', 'externally-sign-sample-agreement');
            return;
        }
        const confirmed = await overlayContext.showModal({
            biName: 'confirm_externally_sign_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                switch(submitType){
                    case SubmitType.ExternallySign:
                        return /*#__PURE__*/ _jsx(ConfirmExternallySignModal, {
                            hasBilledImmediatelyService: hasBilledImmediatelyService(data.services, effectiveDate, getNow()),
                            dismissModal: dismissModal
                        });
                    case SubmitType.SendSilently:
                        return /*#__PURE__*/ _jsx(GenerateLinkModal, {
                            recipientsCount: reviewers.length + compact([
                                existingContact
                            ]).length,
                            dismissModal: dismissModal
                        });
                    case SubmitType.SendProposal:
                        return /*#__PURE__*/ _jsx(ReadyToSendModal, {
                            firstName: firstName,
                            lastName: lastName,
                            companyName: companyName,
                            email: email,
                            reviewers: reviewers,
                            dismissModal: dismissModal
                        });
                    case SubmitType.ScheduleProposal:
                        return /*#__PURE__*/ _jsx(ScheduleProposal, {
                            dismissModal: dismissModal,
                            draftId: draftId
                        });
                }
            }
        });
        if (!confirmed) return;
        if (isSampleRelationship && submitType !== SubmitType.ScheduleProposal) {
            logBI(11901);
            reportToGtm('form-success', 'vendor-sent-test-agreement');
            reportToAppcues('form-success', 'vendor-sent-test-agreement');
            reportToHeap('testAgreement', 'submit', 'vendor-sent-test-agreement');
            reportToHubspot('vendor_sent_a_test_agreement');
        } else if (submitType === SubmitType.SendProposal) {
            logBI(10479, {
                clientEmail: email,
                selectedDay: undefined
            });
            reportProductsFormSubmission('send-agreement-for-approval', [
                prepareFormObject('buyerName', {
                    first: firstName,
                    last: lastName
                }),
                prepareFormObject('buyerEmail', email),
                prepareFormObject('customerName', companyName),
                prepareFormObject('serviceCount', data.services.length),
                prepareFormObject('netTerms', makeNetTerms(data.netTerms)),
                prepareFormObject('isExistingCustomer', data.isExistingCustomer)
            ]);
        }
        overlayContext.startLoader(createRelationshipLoader);
        if (submitType === SubmitType.ScheduleProposal) {
            overlayContext.stopLoader(createRelationshipLoader);
            navigationAfterCreation(routes.draftsDashboard);
            return;
        }
        const result = await handleRequest(createRelationshipFromDraft(getDraftId(), {
            externallySign,
            sendSilently,
            isSampleRelationship
        }));
        if (!result.success) {
            const { text } = result.error.createRelationshipErrorText();
            overlayContext.showErrorToast(text, 'create-relationship-error');
            saveDraft(data, ()=>true);
            overlayContext.stopLoader(createRelationshipLoader);
            return;
        }
        const key = isSampleRelationship ? 'numSampleAgreements' : 'numSaleAgreements';
        const { pending, approved } = (_props_worksAt_key = props.worksAt[key]) !== null && _props_worksAt_key !== void 0 ? _props_worksAt_key : {
            pending: 0,
            approved: 0
        };
        updateWorksAt(isSampleRelationship, externallySign);
        if (!isSampleRelationship && pending === 0 && approved === 0) {
            // Important note: this event should be sent from the backend, as we can't assure that the "worksAt" is correct.
            // It is a technical gap.
            reportToGtm('form-success', 'user-successfully-sent-first-agreement');
            reportToAppcues('form-success', 'user-successfully-sent-first-agreement');
            reportToHubspot('user_successfully_sent_first_agreement_');
            reportToHeap('agreementStudio', 'submit', 'user-successfully-sent-first-agreement');
        }
        overlayContext.stopLoader(createRelationshipLoader);
        history.replace(routes.proposalsDashboard);
        switch(submitType){
            case SubmitType.ExternallySign:
                overlayContext.showSuccessToast(studioTexts.agreementApprovedSuccess(), 'externally-sign-success');
                navigationAfterCreation(routes.agreementsDashboard);
                break;
            case SubmitType.SendSilently:
                try {
                    const recipients = await getAgreementNotificationLinks(result.data);
                    await overlayContext.showModal({
                        biName: 'copy_links_modal',
                        closeOnClickOutside: true,
                        closeOnEscapeKey: true,
                        content: (param)=>{
                            let { dismissModal } = param;
                            return /*#__PURE__*/ _jsx(CopyLinksModal, {
                                dismissModal: dismissModal,
                                recipients: recipients
                            });
                        }
                    });
                    navigationAfterCreation(routes.proposalsDashboard);
                } catch (error) {}
                break;
            case SubmitType.SendProposal:
                try {
                    const notification = await getAgreementNotificationLinks(result.data);
                    const redirectUrl = await overlayContext.showModal({
                        biName: 'post_agreement_creation_modal',
                        closeOnClickOutside: true,
                        closeOnEscapeKey: true,
                        content: (param)=>{
                            let { dismissModal } = param;
                            return /*#__PURE__*/ _jsx(PostAgreementCreation, {
                                testId: "agreement-studio-post-creation-modal",
                                onClose: dismissModal,
                                name: "".concat(firstName, " ").concat(lastName),
                                sampleAgreementData: isSampleRelationship ? {
                                    email: props.userEmail
                                } : undefined,
                                proposalLinks: notification
                            });
                        }
                    });
                    navigationAfterCreation(redirectUrl);
                } catch (error) {
                    sendErrReport('failed to show success modal', error);
                    showAgreementCreationToast(); //fail silently in a reasonable way
                    navigationAfterCreation();
                }
                break;
        }
    }
    const openPreviewModal = (data, resolvedTitle)=>{
        reportButtonClick('agreement-previewed-agreement');
        if (legalTermsLoading || !legalTermsDocuments) return;
        const draft = getDraft(data, resolvedTitle);
        overlayContext.showModal({
            biName: 'save_as_template_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (props)=>/*#__PURE__*/ _jsx(ClientPreview, _object_spread({
                    type: "draft",
                    draftInfo: {
                        draft,
                        sampleContact,
                        contactsMap
                    },
                    entityContext: draftId ? {
                        source: 'draft',
                        id: draftId
                    } : undefined
                }, props))
        });
    };
    async function saveAsTemplate(validateCustomPages) {
        if (draftId == null || linkedRelationshipId != null) return;
        if (!validateCustomPages()) return;
        const success = await overlayContext.showModal({
            biName: 'save_as_template_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(SaveAsTemplateModal, {
                    draftId: draftId,
                    dismissModal: dismissModal
                });
            }
        });
        if (success) history.push(routes.proposalTemplates);
    }
    const getInlineFooterActions = (param)=>{
        let { validateCustomPages } = param;
        return isViewProposal ? [
            _object_spread_props(_object_spread({}, viewProposalMode.sendReminder), {
                bi: {
                    name: 'agreement_studio_send_reminder'
                },
                testId: 'agreement-studio-send-reminder',
                iconStart: /*#__PURE__*/ _jsx(EmailSent, {})
            }),
            _object_spread_props(_object_spread({}, viewProposalMode.copyLink), {
                bi: {
                    name: 'agreement_studio_copy_link'
                },
                testId: 'agreement-studio-copy-link',
                iconStart: /*#__PURE__*/ _jsx(LinkIcon, {})
            })
        ] : [
            {
                bi: {
                    name: 'agreement_studio_save_as_template'
                },
                text: proposalModeTexts.saveAsTemplateButtonLabel(),
                intent: Intent.CreateProposalTemplate,
                testId: 'agreement-studio-save-template',
                disabled: draftId == null || linkedRelationshipId != null,
                onClick: ()=>saveAsTemplate(validateCustomPages),
                tooltipTitle: linkedRelationshipId != null ? proposalModeTexts.saveAsTemplateWithdrawnTooltip() : draftId == null ? proposalModeTexts.saveAsTemplateNoDraftTooltip() : undefined,
                iconStart: /*#__PURE__*/ _jsx(BookmarkAdd, {})
            }
        ];
    };
    const renderSubmitMenuItem = (param)=>{
        let { text, icon, testId, intent, onClick } = param;
        return /*#__PURE__*/ _jsx(MenuItem, {
            testId: testId,
            intent: intent,
            onClick: onClick,
            children: /*#__PURE__*/ _jsxs(Row, {
                gap: 12,
                alignItems: "center",
                children: [
                    icon,
                    /*#__PURE__*/ _jsx("span", {
                        children: text
                    })
                ]
            })
        }, testId);
    };
    const renderSubmitButton = (param)=>{
        let { testId, className, onSubmit, validateTemplateVariables, isSendBlocked } = param;
        const isSendGateActive = isSendBlocked || draftStatus === "loading";
        const showSendBlockedToast = ()=>overlayContext.showInfoToast(proposalModeTexts.sendBlockedSavingToast(), 'send-blocked-saving');
        const submitWithGate = (type)=>{
            if (isSendGateActive) {
                showSendBlockedToast();
                return;
            }
            onSubmit(type);
        };
        if (isViewProposal) {
            const { text, intent, disabled, onClick } = viewProposalMode.edit;
            return /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'edit_view_proposal'
                },
                intent: intent,
                testId: testId,
                className: className,
                disabled: disabled,
                size: isMobileDevice ? 'small' : 'medium',
                onClick: onClick,
                children: text
            });
        }
        if (linkedRelationshipId != null) {
            const approveOnBehalf = async ()=>{
                var _ref;
                if (isSendGateActive) {
                    showSendBlockedToast();
                    return;
                }
                if (!validateTemplateVariables()) return;
                const hasExplicitApproval = ((_ref = draft === null || draft === void 0 ? void 0 : draft.legalTermsDocuments()) !== null && _ref !== void 0 ? _ref : []).some((doc)=>doc != null && doc.termsOfService.type === 'explicitApproval');
                if (hasExplicitApproval) {
                    overlayContext.showErrorToast(studioTexts.approveOnBehalfExplicitApprovalError(), 'approve-on-behalf-explicit-approval-error');
                    return;
                }
                const confirmed = await overlayContext.showModal({
                    biName: 'confirm_externally_sign_modal',
                    closeOnClickOutside: true,
                    closeOnEscapeKey: true,
                    content: (param)=>{
                        let { dismissModal } = param;
                        var _ref;
                        return /*#__PURE__*/ _jsx(ConfirmExternallySignModal, {
                            hasBilledImmediatelyService: hasBilledImmediatelyService((_ref = draft === null || draft === void 0 ? void 0 : draft.services()) !== null && _ref !== void 0 ? _ref : [], effectiveDate, getNow()),
                            dismissModal: dismissModal
                        });
                    }
                });
                if (!confirmed) return;
                const result = await handleRequest(api.relationship.approveOnBehalfOfClient(linkedRelationshipId));
                if (!result.success) {
                    overlayContext.showErrorToast(studioTexts.failedToApproveAgreement(), 'externally-sign-error');
                    return;
                }
                overlayContext.showSuccessToast(studioTexts.agreementApprovedSuccess(), 'externally-sign-success');
                history.push(routes.agreementsDashboard);
            };
            return /*#__PURE__*/ _jsx(SplitButton, {
                label: editInPlaceTexts.republishButton(),
                testId: AgreementStudioProposalModeTestIds.RepublishProposal(),
                className: isMobileDevice ? styles.fitContentBtn : className,
                size: isMobileDevice ? 'small' : 'medium',
                intent: Intent.SendProposal,
                onClick: ()=>submitWithGate(SubmitType.SendProposal),
                popperClassName: styles.popper,
                children: renderSubmitMenuItem({
                    testId: AgreementStudioProposalModeTestIds.ApproveOnBehalf(),
                    icon: /*#__PURE__*/ _jsx(DocumentCheckmark, {}),
                    text: actionsTexts.approveOnBehalf(),
                    intent: Intent.ApproveProposalOnBehalf,
                    onClick: approveOnBehalf
                })
            });
        }
        const submitMenuItems = [
            {
                testId: 'send-proposal',
                icon: /*#__PURE__*/ _jsx(Send, {}),
                text: actionsTexts.sendNowByEmail(),
                intent: Intent.SendProposal,
                onClick: ()=>submitWithGate(SubmitType.SendProposal)
            },
            {
                testId: 'silent-proposal',
                icon: /*#__PURE__*/ _jsx(LinkIcon, {}),
                text: actionsTexts.shareByLink(),
                onClick: ()=>submitWithGate(SubmitType.SendSilently)
            },
            {
                testId: 'schedule-proposal',
                icon: /*#__PURE__*/ _jsx(Calendar, {}),
                text: actionsTexts.scheduleSend(),
                onClick: ()=>submitWithGate(SubmitType.ScheduleProposal)
            },
            {
                testId: 'sign-externally',
                icon: /*#__PURE__*/ _jsx(DocumentCheckmark, {}),
                text: actionsTexts.approveOnBehalf(),
                onClick: ()=>submitWithGate(SubmitType.ExternallySign)
            }
        ];
        if (isMobileDevice) {
            return /*#__PURE__*/ _jsx(Button, {
                bi: {
                    name: 'open_send_proposal_actions_mobile'
                },
                intent: Intent.SendProposal,
                testId: testId,
                className: className,
                size: "iconOnly",
                onClick: ()=>{
                    if (isSendGateActive) {
                        showSendBlockedToast();
                        return;
                    }
                    pushBottomSheetPage({
                        content: /*#__PURE__*/ _jsx(_Fragment, {
                            children: submitMenuItems.map((item)=>renderSubmitMenuItem(_object_spread_props(_object_spread({}, item), {
                                    onClick: ()=>{
                                        popBottomSheetPage();
                                        item.onClick();
                                    }
                                })))
                        })
                    });
                },
                children: /*#__PURE__*/ _jsx(Send, {
                    size: 16
                })
            });
        }
        return /*#__PURE__*/ _jsx(SplitButton, {
            label: proposalModeTexts.send(),
            testId: testId,
            className: className,
            placement: "bottom-end",
            intent: Intent.SendProposal,
            onClick: ()=>submitWithGate(SubmitType.SendProposal),
            popperClassName: styles.popper,
            children: submitMenuItems.map((item)=>renderSubmitMenuItem(item))
        });
    };
    return !legalTermsLoading && !legalTermsError && contactsMap ? /*#__PURE__*/ _jsx(AgreementStudioModal, _object_spread_props(_object_spread({}, props), {
        changedTargets: changedTargets,
        onUnsavedChangesChange: onUnsavedChangesChange,
        draft: draft != null ? draftToCommon(draft) : undefined,
        legalTermsSourceId: draftId,
        getCustomPagesSourceId: genDraftId,
        isSampleRelationship: isSampleRelationship,
        isReadOnly: isViewProposal,
        modalTitle: isViewProposal ? proposalModeTexts.viewProposalTitle() : linkedRelationshipId != null ? proposalModeTexts.editProposalTitle() : proposalModeTexts.createProposalTitle(),
        modalTitleTestId: isViewProposal ? 'view-proposal-title' : linkedRelationshipId != null ? 'edit-proposal-title' : 'create-proposal-title',
        contact: existingContact,
        agreementTitle: agreementTitle,
        legalTermsDocuments: legalTermsDocuments,
        effectiveDate: effectiveDate,
        additionalAutoSaveDependencies: [
            agreementTitle,
            clientContactId,
            effectiveDate,
            reviewers
        ],
        savingStatusMP: isMobileDevice ? null : getSavingStatusMP(draftStatus, proposalModeTexts),
        agreementTitleMP: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(AgreementName, {
                    fullWidth: isMobileDevice,
                    agreementTitle: agreementTitle,
                    setAgreementTitle: setAgreementTitle,
                    setAgreementTitleValid: setTitleValid,
                    showWarning: showAgreementTitleWarning,
                    label: studioTexts.agreementName.label(),
                    disabled: isViewProposal,
                    testId: "agreement-studio-customer-details-input-display-title"
                }),
                /*#__PURE__*/ _jsx(Recipients, {
                    contactDetailsProps: {
                        existingContact,
                        showCustomerDetailsWarning,
                        disabled: isViewProposal || linkedRelationshipId != null,
                        refreshExistingContacts: fetchContacts,
                        onValidation: setNewContactValid,
                        onChange: (change)=>{
                            setIsSampleRelationship(change.type === 'sampleContact');
                            setClientContactId(change.type === 'existingContact' ? change.id : null);
                            if (change.type === 'existingContact') {
                                setShowCustomerDetailsWarning(false);
                            }
                        }
                    },
                    reviewers: reviewers,
                    showReviewerErrors: showReviewerErrors,
                    isSampleRelationship: isSampleRelationship,
                    onReviewersChange: setReviewers,
                    additionalRecipientsDisabled: isViewProposal,
                    amendmentInfoCallout: existingContact && !isViewProposal && contactHasAgreement(existingContact, agreements, proposals) && /*#__PURE__*/ _jsxs(Callout, {
                        children: [
                            studioTexts.contactHasAgreementInfo({
                                name: existingContact.companyName()
                            }),
                            ' ',
                            /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                bi: {
                                    name: 'agreement_studio_amendment_info_learn_more'
                                },
                                openInNewTab: true,
                                href: helpArticles.howToEditAgreement(),
                                testId: "agreement-studio-amendment-info",
                                children: studioTexts.contactHasAgreementLearnMore()
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(RelationshipEffectiveDate, {
                    value: effectiveDate,
                    disabled: isViewProposal,
                    onChange: setEffectiveDate,
                    title: proposalModeTexts.effectiveDateTitle(),
                    description: proposalModeTexts.effectiveDateDescription(),
                    dataAgentTarget: AGENT_TARGET_EFFECTIVE_DATE
                })
            ]
        }),
        inlineFooterActions: getInlineFooterActions,
        additionalFooterActions: isViewProposal ? [
            _object_spread_props(_object_spread({}, viewProposalMode.duplicate), {
                bi: {
                    name: 'agreement_studio_duplicate_proposal'
                },
                iconStart: /*#__PURE__*/ _jsx(Copy, {}),
                testId: 'agreement-studio-duplicate-proposal'
            }),
            _object_spread_props(_object_spread({}, viewProposalMode.cancel), {
                bi: {
                    name: 'agreement_studio_cancel_proposal'
                },
                iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                skin: 'error',
                testId: 'agreement-studio-cancel-proposal'
            })
        ] : compact([
            linkedRelationshipId == null && {
                bi: {
                    name: 'agreement_studio_archive_draft'
                },
                text: actionsTexts.archiveDraft(),
                disabled: draftId == null || (draft === null || draft === void 0 ? void 0 : draft.scheduledDate()) != null,
                iconStart: /*#__PURE__*/ _jsx(Archive, {}),
                testId: AgreementStudioProposalModeTestIds.ArchiveDraft(),
                onClick: ()=>{
                    archive(draftId);
                }
            },
            linkedRelationshipId == null && {
                bi: {
                    name: 'agreement_studio_discard_draft'
                },
                text: actionsTexts.discardDraft(),
                skin: 'error',
                disabled: draftId == null,
                iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                testId: 'agreement-studio-topbar-discard-draft',
                onClick: discardDraft
            },
            linkedRelationshipId != null && {
                bi: {
                    name: 'agreement_studio_cancel_edit_proposal'
                },
                text: editInPlaceTexts.cancelEditFooterAction(),
                skin: 'error',
                iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                testId: AgreementStudioProposalModeTestIds.CancelEdit(),
                onClick: ()=>cancelEditProposal(linkedRelationshipId)
            }
        ]),
        onSubmitCb: submit,
        onAutoSaveCb: isViewProposal ? undefined : saveDraft,
        openPreviewModal: openPreviewModal,
        renderSubmitButton: renderSubmitButton
    })) : /*#__PURE__*/ _jsx(FunctionalLoader, {
        show: true
    });
}
